'use client';

import { useTransition } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter, usePathname } from '@/lib/i18n/navigation';
import { otherLocale, type Locale } from '@/lib/i18n/routing';
import { updateUserLocale } from '@/actions/account/update-locale';
import { cn } from '@/lib/utils';

interface LocaleSwitcherProps {
  variant?: 'header' | 'footer';
  className?: string;
}

/**
 * Toggle de idioma `ES ↔ EN`. Hace `router.replace()` al mismo pathname
 * con el otro locale; next-intl persiste la elección en cookie `NEXT_LOCALE`.
 *
 * Variantes:
 *  - `header`: dos chips compactos lado a lado (botón fino).
 *  - `footer`: línea sutil con el otro idioma como CTA.
 */
export function LocaleSwitcher({ variant = 'header', className }: LocaleSwitcherProps) {
  const t = useTranslations('common');
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();

  const switchTo = (target: Locale) => {
    if (target === locale) return;
    startTransition(() => {
      router.replace(pathname, { locale: target });
      // refresh fuerza a que client components que leen useLocale() se rehidraten
      // junto con server components, evitando que header/footer queden en el
      // locale anterior tras el navigate.
      router.refresh();
    });
    // Persistir locale del user logueado en DB (best-effort, no bloquea UI)
    void updateUserLocale(target).catch(() => {});
  };

  if (variant === 'footer') {
    const target = otherLocale(locale);
    const label = target === 'en-US' ? t('english') : t('spanish');
    return (
      <button
        type="button"
        onClick={() => switchTo(target)}
        disabled={isPending}
        aria-label={t('changeLanguage')}
        className={cn(
          'type-caption text-[var(--color-fg-subtle)] underline-offset-4 transition-colors hover:text-[var(--color-accent)] hover:underline',
          isPending && 'opacity-50',
          className
        )}
      >
        {label}
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label={t('changeLanguage')}
      className={cn(
        'inline-flex items-center rounded-xs border border-[var(--color-border)] p-0.5',
        className
      )}
    >
      {(['es-MX', 'en-US'] as const).map((l) => {
        const active = l === locale;
        return (
          <button
            key={l}
            type="button"
            onClick={() => switchTo(l)}
            disabled={isPending || active}
            aria-pressed={active}
            aria-label={l === 'en-US' ? t('english') : t('spanish')}
            className={cn(
              'type-caption rounded-xs px-2 py-1 transition-colors',
              active
                ? 'bg-[var(--color-fg)]/10 text-[var(--color-fg)]'
                : 'text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]'
            )}
          >
            {l === 'en-US' ? 'EN' : 'ES'}
          </button>
        );
      })}
    </div>
  );
}
